/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2023-01-11 14:21:40
 *
 *******************************************************/
// @flow
import _ from 'lodash';
import React, { Component } from 'react';
import { FormLayout, TextStyle, Link, Icon } from '@shopify/polaris';
import { ExternalMinor } from '@shopify/polaris-icons';
import { Field } from 'redux-form';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';
import RenderColorPickerItem from 'src/components/views/common/render-color-picker-item';
import { RenderRadioButton2 } from 'src/components/views/common/forms/render-radio-button-2';
import { DropdownIndicator, styles } from './react-select-style';
import { RenderSelect2 } from 'src/components/views/common/forms/render-select-2-v2';
import { RenderCheckbox } from 'src/components/views/common/forms/render-checkbox';
import { DEFAULT_FONT_SIZE } from 'src/constants/theme-settings';
import { IMAGE_SIZE_OPTIONS, PARALLAX_DIRECTION_OPTIONS } from 'src/constants/theme-settings.js';
import { URL } from 'src/constants/links';

type Props = {
  formValues: any,
  updateField: (field: string, value: any) => void
};

type ComposedProps = Props & WithI18nProps;

class CollectionHeaderImageForm extends Component<ComposedProps> {
  state = {
    isOpenedCheckout: true,
    isOpenedViewCart: true
  };

  /**
   * Blur text field
   * @param {*} event
   * @param {*} value
   * @param {*} name
   */
  onBlurTextField = (event, value, name) => {
    const { updateField } = this.props;
    if (!value) {
      event.preventDefault();
      updateField(name, DEFAULT_FONT_SIZE);
    }
  };

  /**
   * Get layout option with translation label
   * @returns
   */
  getImageSizes = () => {
    const { i18n } = this.props;
    return IMAGE_SIZE_OPTIONS.map(option => {
      const { label, value } = option;

      return {
        label: (
          <div>
            <p>{i18n.translate(label)}</p>
            {/* {helpText && <p className="help-text">{helpText}</p>} */}
          </div>
        ),
        value
      };
    });
  };

  /**
   * Get color picker options
   * @returns
   */
  getColorPickers = (prefix: string) => {
    const { i18n, formValues } = this.props;

    return [
      {
        colorValue: _.get(formValues, `${prefix}.overlayColor`),
        fieldName: `${prefix}.overlayColor`,
        fieldLabel: i18n.translate('AdminHTML.ThemeSetting.Form.collectionHeaderImage_Color_Item2')
      }
    ];
  };

  render() {
    const { i18n, prefix, updateField, formValues } = this.props;

    const isOriginal = _.get(formValues, `${prefix}.size`) === 'original';
    const parallaxEffectChecked = _.get(formValues, `${prefix}.parallaxEffect`);

    return (
      <FormLayout>
        <div>
          <span className="form-link">
            <TextStyle variation="subdued">
              {i18n.translate('AdminHTML.ThemeSetting.Form.collectionHeaderImage_UserGuide')}
            </TextStyle>{' '}
            <Link url={URL['CollectionImageThemeSetting']} external>
              {i18n.translate('AdminHTML.ThemeSetting.Common.clickHere')}
              <Icon source={ExternalMinor} color="base" />
            </Link>
            <span style={{ color: '#2C6ECB' }}>.</span>
          </span>
        </div>

        <div className="form-section">
          <div className="form-section__item">
            <Field
              name={`${prefix}.size`}
              component={RenderSelect2}
              options={this.getImageSizes()}
              label={i18n.translate('AdminHTML.ThemeSetting.Form.collectionHeaderImage_Size_Label')}
              components={{ DropdownIndicator }}
              styles={styles}
              isSearchable={false}
            />
          </div>

          {!isOriginal && (
            <div className="form-section__item form-section__item--standard-spacing">
              <Field
                name={`${prefix}.parallaxEffect`}
                component={RenderCheckbox}
                label={i18n.translate(
                  'AdminHTML.ThemeSetting.Form.collectionHeaderImage_Checkbox_Label'
                )}
              />
            </div>
          )}

          {parallaxEffectChecked && !isOriginal && (
            <div className="form-section__item">
              <p>
                {i18n.translate(
                  'AdminHTML.ThemeSetting.Form.collectionHeaderImage_Direction_Label'
                )}
              </p>
              <div
                className="radio-button-wrapper radio-button-wrapper--no-gap radio-button-wrapper--bordered"
                style={{ marginTop: '5px' }}>
                {PARALLAX_DIRECTION_OPTIONS.map((item, index) => (
                  <Field
                    key={index}
                    name={`${prefix}.directionParallax`}
                    id={item.value}
                    component={RenderRadioButton2}
                    label={i18n.translate(item.label)}
                    disabled={!parallaxEffectChecked || isOriginal}
                  />
                ))}
              </div>
            </div>
          )}

          <div className="form-section" style={{ marginTop: '12px' }}>
            <p className="title title-small">
              {i18n.translate('AdminHTML.ThemeSetting.Form.color_Title')}
            </p>
            {this.getColorPickers(prefix).map((item, index) => (
              <RenderColorPickerItem
                key={index}
                colorValue={item.colorValue}
                fieldName={item.fieldName}
                fieldLabel={item.fieldLabel}
                updateField={updateField}
              />
            ))}
          </div>
        </div>
      </FormLayout>
    );
  }
}

export default withI18n()(CollectionHeaderImageForm);
